import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import {
	Icon,
	TEXT_ALIGNMENT_OPTIONS,
	TextAlignmentOptions,
	BG_IMAGE_ALIGNMENT_OPTIONS,
	BgImageAlignmentOptions,
	Url,
} from '#const';
import { getIconUrl } from '#utils';

const BulletedImageList = dynamic(() => import('./BulletedImageList'));

export const defaultOptions = {
	heading: '<h2>Why Choose Us?</h2>',
	headingAlignment: TextAlignmentOptions.CENTER,
	headingSpacing: 10,
	items: [
		{
			icon: Url.DEFAULT_ICON,
			heading: 'Expert Guidance',
			subheading: 'Professional advice from experienced specialists',
		},
		{
			icon: Url.DEFAULT_ICON,
			heading: 'Experienced Team',
			subheading: 'Decades of combined experience in injury law',
		},
		{
			icon: Url.DEFAULT_ICON,
			heading: 'Results-Focused',
			subheading: 'We fight for the compensation you deserve',
		},
	],
	itemAlignment: BgImageAlignmentOptions.TOP,
	color: '#F9FAFB',
	line: false,
} as const;

Builder.registerComponent(BulletedImageList, {
	name: 'BulletedImageList',
	inputs: [
		{
			name: 'heading',
			friendlyName: 'Heading Text',
			required: false,
			type: 'richText',
			defaultValue: defaultOptions.heading,
		},
		{
			name: 'headingAlignment',
			friendlyName: 'Heading Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: defaultOptions.headingAlignment,
		},
		{
			name: 'headingSpacing',
			friendlyName: 'Heading Bottom Spacing',
			type: 'number',
			defaultValue: defaultOptions.headingSpacing,
			min: 0,
			max: 50,
			step: 5,
			helperText:
				'Spacing below the heading (0 for no spacing, or 5, 10, 15, 20, 25, etc. from CSS spacing scale)',
		},
		{
			name: 'items',
			type: 'list',
			required: true,
			subFields: [
				{
					name: 'icon',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
					required: false,
				},
				{
					name: 'heading',
					type: 'string',
					required: true,
				},
				{
					name: 'subheading',
					type: 'string',
					required: false,
				},
			],
			defaultValue: [...defaultOptions.items],
		},
		{
			name: 'itemAlignment',
			friendlyName: 'Item Vertical Alignment',
			type: 'string',
			enum: [...BG_IMAGE_ALIGNMENT_OPTIONS].filter((option) =>
				['Top', 'Center', 'Bottom'].includes(option),
			),
			defaultValue: defaultOptions.itemAlignment,
			helperText: 'Vertical alignment of content within each list item',
		},
		{
			name: 'color',
			friendlyName: 'Background Color',
			type: 'color',
			defaultValue: defaultOptions.color,
		},
		{
			name: 'line',
			friendlyName: 'Dividing Line',
			type: 'boolean',
			defaultValue: defaultOptions.line,
			helperText: 'Include dividing line under the headline?',
		},
	],
	canHaveChildren: true,
	defaultChildren: [
		{
			'@type': '@builder.io/sdk:Element',
			component: {
				name: 'Button',
				options: {
					copy: 'Get Started',
					arrow: true,
					color: '#089BAB',
				},
			},
		},
	],
	childRequirements: {
		message: 'Child must be a Button.',
		query: { 'component.name': { $eq: 'Button' } },
	},
	image: getIconUrl(Icon.IMAGE_TEXT_SECTION),
});
